import { lazy, Suspense, type ComponentProps } from "react";
import { Link, useInRouterContext } from "react-router";
import { cn } from "@/lib/utils";

interface ChatMarkdownProps {
  content: string;
  className?: string;
}

const PROSE_CLASSES = [
  "prose prose-sm max-w-none",
  "prose-headings:mb-2 prose-headings:mt-4 prose-headings:font-semibold",
  "prose-h2:text-base prose-h3:text-sm",
  "prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5",
  // text-foreground override: Tailwind prose defaults pre to gray-200 for a dark bg we don't use.
  "prose-pre:bg-muted prose-pre:text-foreground prose-pre:text-xs",
  "prose-table:my-3 prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2",
  "prose-table:text-xs prose-th:font-semibold",
] as const;

type MarkdownLinkProps = ComponentProps<"a"> & { node?: unknown };

/**
 * A link to a path in the app is followed by the router, so the legal documents
 * can point at one another without reloading the whole app. Anything else, and
 * anything rendered outside a router, stays an ordinary anchor.
 */
function MarkdownLink({ href, node: _node, ...props }: MarkdownLinkProps) {
  const inRouter = useInRouterContext();
  if (inRouter && href?.startsWith("/") && !href.startsWith("//")) {
    return <Link to={href} {...props} />;
  }
  return <a href={href} {...props} />;
}

const MARKDOWN_COMPONENTS = { a: MarkdownLink };

// Lazy-load the heavy markdown rendering (react-markdown + remark-gfm ~60-80 KB)
const LazyMarkdownContent = lazy(() =>
  Promise.all([import("react-markdown"), import("remark-gfm")]).then(
    ([markdownMod, remarkGfmMod]) => ({
      default: ({ content, className }: ChatMarkdownProps) => {
        const Markdown = markdownMod.default;
        return (
          <div className={cn(...PROSE_CLASSES, className)}>
            <Markdown
              remarkPlugins={[remarkGfmMod.default]}
              components={MARKDOWN_COMPONENTS}
            >
              {content}
            </Markdown>
          </div>
        );
      },
    }),
  ),
);

export function ChatMarkdown({ content, className }: ChatMarkdownProps) {
  return (
    <Suspense
      fallback={
        <div
          className={cn(...PROSE_CLASSES, "text-muted-foreground", className)}
        >
          {content.slice(0, 300)}
          {content.length > 300 ? "\u2026" : ""}
        </div>
      }
    >
      <LazyMarkdownContent content={content} className={className} />
    </Suspense>
  );
}
